// The announcer's lines on the Victory and Roasted screens and on share cards. Hand written, picked from the battle
// id, so both players, every reload and the shared card show the same one. Lexicon: no betting, casino, leverage or
// profit words (see BANNED); "staking", "battling", "price prediction" and "victory payout" are fine.

export interface AnnounceVars {
  /** The player the line is about, as other players see them. */
  me: string;
  opp: string;
  /** "LONG" or "SHORT". */
  side: string;
  arena: string;
  /** The price move over the battle, in percent. */
  pct: number;
  blitz: boolean;
}

type Line = string | { when: "blitz" | "big" | "tiny"; text: string };

// {me} {opp} {side} {arena} {move} are filled in. Win lines are about the winner; loss lines talk to the player.
export const WIN_LINES: Line[] = [
  "{me} called it before the chart did. {opp} is still reading the candles.",
  "{me} saw that move coming from a mile away. {opp} is still looking for the exit!",
  "Clean read by {me}. {opp} needs a minute.",
  "{me} said {side} and the market said yes sir.",
  "{me} didn't guess. {me} knew.",
  "Chart reader of the year: {me}. Runner-up: not {opp}.",
  "{opp} brought vibes. {me} brought a read.",
  "{me} called {side} like it was written in the stars.",
  "That's not luck, that's {me} speaking fluent {arena}.",
  "{me} made {arena} look easy. {opp} made it look hard.",
  "{me} read the room. {opp} read the wrong room.",
  "Somebody check on {opp}. {me} just cooked.",
  "{me} pressed {side} and never looked back.",
  "{me}: one call, zero doubts.",
  "The candles bowed to {me} today.",
  "{opp} blinked. {me} didn't.",
  "{me} had the answer key. {opp} had a pencil.",
  "{me} went {side} and the chart followed like a fan.",
  "Another day, another clean read from {me}.",
  "{me} is not in the arena. {me} is the arena.",
  "{me} called it. Screenshot it. Frame it.",
  "{opp} is drafting an apology to the chart.",
  "{me} saw the move before the move saw itself.",
  "Cold. Calm. Correct. That's {me}.",
  "{me} just turned {arena} into a victory lap.",
  "{opp} picked a side. {me} picked the right one.",
  "Respect the read. {me} earned it.",
  "{me} stayed patient and the price paid out.",
  "The market whispered and only {me} heard it.",
  "{me} 1, {opp} 0. Rematch button is lonely.",
  "{me} said {side}, the candles said copy that.",
  "Textbook. Chapter one: whatever {me} just did.",
  "{opp} wanted a battle. {me} gave a lesson.",
  "{me} didn't sweat once. The chart did.",
  "That read had main character energy. Nice, {me}.",
  "{me} out here doing price prediction like a weather app that's always right.",
  "{me} picked {side} and {arena} picked {me}.",
  "Called. Locked. Collected. {me} wins.",
  "{opp} is still refreshing the chart. It won't change.",
  "{me} watched the candles like a hawk and struck like one too.",
  "Victory payout incoming for {me}. Tissues incoming for {opp}.",
  "{me} has seen this movie before and knew the ending.",
  "{me} went {side} with zero hesitation. Rewarded.",
  "{opp} needed a different side. {me} needed nothing.",
  "The chart is {me}'s love language.",
  "{me} just made {opp} a fan.",
  "Calm hands, sharp read. {me} takes it.",
  "{me}: certified chart whisperer.",
  "Read it, called it, won it. Thanks for coming, {opp}.",
  "{me} made the call and {arena} made it real.",
  "Some people predict. {me} delivers.",
  "{opp} chose chaos. {me} chose correct.",
  "That's how you open a battle and close it. Well played, {me}.",
  "The arena belongs to {me} for the next five minutes at least.",
  "{me} caught that move like a cat with a laser pointer.",
  "{me} turned a coin toss into a sure thing. Not a coin toss.",
  "{opp} is googling what just happened. {me} knows.",
  "{me}'s read was sharper than {arena}'s last candle.",
  "Big brain {side}. {me} walks away clean.",
  "{me} saw green. {me} got green.",
  "Victory looks good on {me}.",
  "{me} had one job and nailed it.",
  "{opp} wants to know {me}'s secret. It's the chart. It's always the chart.",
  "That's a highlight reel read from {me}.",
  "{me} just added another notch to the belt.",
  "{opp} came to battle. {me} came to win.",
  "{me}'s {side} call aged like fine wine. In minutes.",
  "Run it back? {opp} might want to stretch first.",
  "{me} stays undefeated in this conversation.",
  "{me} plays chess while the chart plays checkers.",
  "Easy money? No. Earned read. {me}.",
  "{me} said trust me. The chart trusted {me}.",
  "{opp} took a nap. {me} took the battle.",
  "That's a {me} special: one read, one win.",
  "Nobody called {side} like {me} called {side}.",
  "{me} is on a different timeframe than the rest of us.",
  "{opp} is still processing. {me} is still celebrating.",
  "Somebody give {me} a microphone. That read was a speech.",
  "{me} didn't chase the price. The price came to {me}.",
  { when: "blitz", text: "One minute was all {me} needed." },
  { when: "blitz", text: "Blitz master {me} strikes again." },
  { when: "blitz", text: "Sixty seconds, one read, one winner: {me}." },
  { when: "blitz", text: "{opp} was still loading. {me} already won the Blitz." },
  { when: "blitz", text: "Fast hands, faster read. {me} owns the Blitz." },
  { when: "blitz", text: "{me} read a one minute chart like a novel." },
  { when: "blitz", text: "Blitz? More like {me}-litz." },
  { when: "blitz", text: "In and out in a minute. Clean, {me}." },
  { when: "big", text: "{arena} moved {move} and {me} was on the right side of all of it." },
  { when: "big", text: "A {move} swing and {me} rode it like a pro." },
  { when: "big", text: "{arena} went wild ({move}). {me} stayed calm." },
  { when: "big", text: "{me} saw a {move} move coming. Who does that?" },
  { when: "big", text: "That {move} candle had {me}'s name on it." },
  { when: "big", text: "{opp} watched {arena} run {move} the wrong way. Ouch." },
  { when: "big", text: "Big move, bigger read. {me} wins." },
  { when: "big", text: "{arena} said {move}. {me} said I know." },
  { when: "tiny", text: "Won by a hair. A hair is enough. {me} takes it." },
  { when: "tiny", text: "Photo finish! {me} edges it." },
  { when: "tiny", text: "{arena} barely moved and {me} still read it right. Precision." },
  { when: "tiny", text: "The slimmest of margins. The cleanest of wins. {me}." },
  { when: "tiny", text: "{opp} lost by inches. {me} won by a read." },
  { when: "tiny", text: "A tiny move, a huge win for {me}." },
  { when: "tiny", text: "Down to the last tick. {me} survives." },
  { when: "tiny", text: "Margin so small you need a microscope. {me} doesn't care." },
];

export const LOSS_LINES: Line[] = [
  "You called {side} with your whole chest. Bold. Wrong, but bold.",
  "The chart had other plans. {opp} read it better this time.",
  "{opp} took this one. The rematch button is right there.",
  "All that hope, one second of truth. Run it back?",
  "You zigged. {arena} zagged.",
  "Plot twist: the chart disagreed.",
  "{opp} saw it. You saw something else.",
  "That {side} call is in the museum of almosts now.",
  "The candles said no. Politely. Then not politely.",
  "You were early. Or wrong. History will decide. Mostly wrong.",
  "Respectfully, {arena} did not care about your {side}.",
  "{opp} just walked off with your stake and your pride.",
  "Shake it off. Champions lose too. Then they run it back.",
  "The read was brave. The chart was braver.",
  "You and {arena} need to talk.",
  "Somewhere a candle is laughing.",
  "{opp}'s read: correct. Yours: character building.",
  "You called {side}. The chart called your bluff.",
  "Unlucky? Maybe. Run it back and find out.",
  "{opp} was on the other side and the other side was right.",
  "Pain. Temporary. Rematch: available.",
  "That's the chart's way of saying try again.",
  "You blinked. {opp} didn't.",
  "Every legend has an L. Today was yours.",
  "The arena giveth. The arena taketh. Today it took.",
  "{opp} is screenshotting this. Just so you know.",
  "You picked a side. It wasn't the winning one.",
  "Your {side} call needed one more candle. It didn't get it.",
  "Brave call. The wrong kind of brave.",
  "{opp} says thanks for the donation.",
  "A loss is just a lesson with a price tag.",
  "You'll get the next one. Probably. Run it back.",
  "The chart went one way and your hopes went the other.",
  "{arena} looked at your {side} and said not today.",
  "That one stings. Ice it with a rematch.",
  "You came for glory and left with a story.",
  "{opp} owes the chart a thank you note.",
  "The salt meter just hit max.",
  "Your read and {arena} are no longer on speaking terms.",
  "You were so close. The chart was closer to {opp}.",
  "Take a breath. Then take your revenge.",
  "Good battle, wrong side.",
  "Even the best traders of candles get roasted. Today: you.",
  "{opp} is doing a victory lap around your {side}.",
  "That {side} looked good on paper. Paper lost.",
  "The chart wrote a story and you weren't the hero.",
  "Every L is a setup for a comeback arc.",
  "You and {opp} both had a plan. Only one plan worked.",
  "Sometimes the chart just picks violence.",
  "Hold your head high. Then hit rematch.",
  "{opp} read the tea leaves. You read the tea.",
  "The market said hold my drink.",
  "You called it, just in the opposite direction.",
  "This is the part of the movie before the comeback.",
  "{arena} took your {side} personally.",
  "Not your battle. Next one could be.",
  "{opp} is unbearable now. Fix it with a rematch.",
  "The roast is served. Extra salty.",
  "Your conviction was strong. The chart's was stronger.",
  "That's a lesson in humility, sponsored by {arena}.",
  "You had a feeling. The feeling lied.",
  "The candles staged an intervention.",
  "{opp} took the W. You took notes. Use them.",
  "Wrong side, right attitude. Run it back.",
  "Down but not out. Probably.",
  "You and the chart have unfinished business.",
  "The scoreboard is cruel today.",
  "{opp}'s victory payout thanks you for your service.",
  "That {side} call will make a great comeback story.",
  "The chart said plot armor is for {opp} today.",
  "You came in hot. {arena} came in cold.",
  "One bad read doesn't make a bad reader. Two might.",
  "Regroup. Reread. Rematch.",
  "The roast was inevitable. The rematch is optional.",
  "You fought the chart and the chart won.",
  "{opp} played it smart. You played it loud.",
  "Somebody tell your {side} it's over.",
  "Take the L, keep the swagger.",
  { when: "blitz", text: "One minute of hope, one second of pain." },
  { when: "blitz", text: "Blitz giveth, Blitz taketh. Today it took." },
  { when: "blitz", text: "Sixty seconds was not enough for your {side}." },
  { when: "blitz", text: "Fast battle, fast L. Fast rematch?" },
  { when: "blitz", text: "That Blitz went by faster than your confidence." },
  { when: "blitz", text: "Blitz rules: blink and you're roasted." },
  { when: "blitz", text: "{opp} won the speed round. Your move." },
  { when: "blitz", text: "A minute is a long time in {arena}. You found out." },
  { when: "big", text: "{arena} moved {move} the wrong way. Brutal." },
  { when: "big", text: "A {move} swing against you. The chart chose violence." },
  { when: "big", text: "You picked {side} right before a {move} plot twist." },
  { when: "big", text: "{move} the other way. Not even close. Respect the chart." },
  { when: "big", text: "{arena} ran {move} and your {side} ran out of road." },
  { when: "big", text: "That {move} candle had {opp}'s name on it." },
  { when: "big", text: "When {arena} moves {move}, you'd better be on the right side. You weren't." },
  { when: "big", text: "Big move, wrong way. The roast is well done." },
  { when: "tiny", text: "Lost by a hair. The most painful kind of L." },
  { when: "tiny", text: "Photo finish, and the photo wasn't kind to you." },
  { when: "tiny", text: "So close the chart needed a magnifying glass. Still an L." },
  { when: "tiny", text: "One tick. One single tick. Run it back." },
  { when: "tiny", text: "The margin was tiny. The salt is huge." },
  { when: "tiny", text: "Almost had it. Almost doesn't pay." },
  { when: "tiny", text: "Down to the last tick and the tick wasn't yours." },
  { when: "tiny", text: "{opp} won by inches. Go get those inches back." },
];

/** Words that never appear in a line, a caption or anything a player reads. */
export const BANNED = ["bet", "betting", "casino", "gamble", "gambling", "wager", "odds", "jackpot", "leverage", "profit"];

/**
 * A roast line on a card other people see. The lines talk to the player ("your LONG"), which reads as if it were about
 * the viewer, so on a card the announcer says the player's name first: "SMART_ECH, sixty seconds was not enough...".
 */
export function addressed(line: string, name: string): string {
  const [first = ""] = line.split(" ");
  // Names and sides are in capitals and stay so; any other opening word drops its capital after the comma.
  const keep = first === first.toUpperCase() || first.startsWith("I'") || first === "I";
  return `${name}, ${keep ? line : line[0]!.toLowerCase() + line.slice(1)}`;
}

/** How the announcer names a player: their username or first name, in capitals, without the @. */
export function announcerName(p: { username: string | null; firstName: string } | null): string {
  return (p ? p.username ?? p.firstName : "player").toUpperCase();
}

function hash(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

/** The battle's line. A line that fits it (Blitz, a big move, a photo finish) is picked half the time when one exists. */
export function announce(won: boolean, battleId: string, v: AnnounceVars): string {
  const lines = won ? WIN_LINES : LOSS_LINES;
  const move = Math.abs(v.pct);
  const fits = (l: Line) => typeof l !== "string" && ((l.when === "blitz" && v.blitz) || (l.when === "big" && move >= 0.2) || (l.when === "tiny" && move < 0.02));
  const h = hash(battleId);
  const special = lines.filter(fits);
  const plain = lines.filter((l) => typeof l === "string");
  const pool = special.length && h % 2 === 0 ? special : plain;
  const line = pool[Math.floor(h / 2) % pool.length]!;
  const text = typeof line === "string" ? line : line.text;
  return text
    .replaceAll("{me}", v.me)
    .replaceAll("{opp}", v.opp)
    .replaceAll("{side}", v.side)
    .replaceAll("{arena}", v.arena)
    .replaceAll("{move}", `${move.toFixed(2)}%`);
}
